import React, { useState } from 'react';
import { View, Text } from 'react-native';
import { Calendar } from 'react-native-calendars';
import AdminInput from '../AdminInput';
import { ADMIN_COLORS } from '../../config/theme/adminColors';
import {
  Field,
  FieldLabel,
  InfoBanner,
} from '../../pages/AdminPanel/Products/formStyles';

// Data + hora de início + hora de término da aula ao vivo.
//
// Zoom e Panda pedem exatamente os mesmos campos, com a mesma regra de
// clamp/recalculo — antes eram dois blocos de ~150 linhas copiados no
// EditVideo, e só o do Zoom tinha sido modernizado. Fica aqui, em nível de
// módulo (e não dentro do EditVideo), porque um componente declarado dentro de
// outro é recriado a cada render e o input perde o foco a cada tecla digitada.

const pad = (value) => String(value).padStart(2, '0');

const clamp = (text, max) =>
  Math.min(max, Math.max(0, parseInt(text, 10) || 0));

const toDateKey = (date) =>
  `${date.getFullYear()}-${pad(date.getMonth() + 1)}-${pad(date.getDate())}`;

// Um dos dois campos (HH ou MM) de um horário.
//
// O texto digitado vive em estado local enquanto o campo está em edição: o
// valor vindo das props é sempre normalizado em dois dígitos ("00"), e com
// `maxLength={2}` isso deixava o input permanentemente cheio — não dava para
// digitar sem apagar antes, e o apagar era desfeito na hora pelo re-pad ("0"
// volta a virar "00"). Colar funcionava porque substitui a seleção inteira de
// uma vez. Aqui o texto cru é preservado durante a digitação e só volta a ser
// normalizado no blur.
const TimeField = ({ value, max, placeholder, onChange }) => {
  const [draft, setDraft] = useState(null);

  const handleChangeText = (text) => {
    const digits = text.replace(/[^0-9]/g, '').slice(0, 2);
    const next = clamp(digits, max);
    // Só o estouro do máximo (ex.: "95" na hora) é corrigido já na digitação,
    // para o campo nunca mostrar algo diferente do que foi salvo.
    setDraft(parseInt(digits, 10) > max ? pad(next) : digits);
    onChange(next);
  };

  return (
    <AdminInput
      keyboardType="numeric"
      placeholder={placeholder}
      maxLength={2}
      selectTextOnFocus
      value={draft === null ? pad(value) : draft}
      onChangeText={handleChangeText}
      onBlur={() => setDraft(null)}
    />
  );
};

// HH : MM lado a lado em qualquer largura (o `Row` do formStyles empilha no
// mobile, o que não faz sentido para um campo de hora).
const TimeInput = ({ hour, minute, onChange }) => (
  <View style={{ flexDirection: 'row', alignItems: 'center' }}>
    <View style={{ flex: 1, minWidth: 0 }}>
      <TimeField
        value={hour}
        max={23}
        placeholder="HH"
        onChange={(next) => onChange({ hour: next, minute })}
      />
    </View>
    <Text
      style={{
        fontSize: 16,
        marginHorizontal: 8,
        marginTop: 6,
        color: ADMIN_COLORS.ink,
      }}
    >
      :
    </Text>
    <View style={{ flex: 1, minWidth: 0 }}>
      <TimeField
        value={minute}
        max={59}
        placeholder="MM"
        onChange={(next) => onChange({ hour, minute: next })}
      />
    </View>
  </View>
);

export function LiveScheduleFields({
  dateLabel = 'Data da aula ao vivo',
  liveDate,
  startHour,
  startMinute,
  endHour,
  endMinute,
  onChangeDate,
  onChangeStart,
  onChangeEnd,
  renderArrow,
}) {
  return (
    <>
      <Field>
        <FieldLabel>{dateLabel}</FieldLabel>
        <InfoBanner>
          Ao informar esses dados e habilitar o Calendário no App Mobile, seus
          alunos terão acesso aos eventos ao vivo dentro do próprio calendário.
        </InfoBanner>
        <View style={{ marginTop: 12 }}>
          <Calendar
            onDayPress={({ day, month, year }) => {
              const onlyDate = new Date(year, month - 1, day);
              onlyDate.setHours(0, 0, 0, 0);
              onChangeDate(onlyDate);
            }}
            {...(liveDate && {
              markedDates: {
                [toDateKey(liveDate)]: {
                  selected: true,
                  selectedColor: '#008000',
                },
              },
            })}
            renderArrow={renderArrow}
          />
        </View>
      </Field>

      <Field>
        <FieldLabel>Hora de início</FieldLabel>
        <TimeInput
          hour={startHour}
          minute={startMinute}
          onChange={onChangeStart}
        />
      </Field>

      <Field>
        <FieldLabel>Hora de término</FieldLabel>
        <TimeInput hour={endHour} minute={endMinute} onChange={onChangeEnd} />
      </Field>
    </>
  );
}
